import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Lock, Share2, FileText, Library } from "lucide-react";

const ATTACHMENTS = [
  {
    key: "biblioteca",
    label: "Biblioteca completa",
    short: "Biblioteca",
    file: "/docs/protocolo-2026/club-viajemos-biblioteca-completa.pdf",
    anchor: "onboarding",
    icon: Library,
    color: "#1D1D1B",
    bg: "#F5F5F4",
  },
  {
    key: "s1",
    label: "Secreto 1 — Certificados de Viaje",
    short: "S1 · Certificados",
    file: "/docs/protocolo-2026/S1-certificados-de-viaje.pdf",
    anchor: "s1",
    icon: FileText,
    color: "#E63946",
    bg: "#FCE4E6",
  },
  {
    key: "s2",
    label: "Secreto 2 — Asesorías Migratorias",
    short: "S2 · Migratorias",
    file: "/docs/protocolo-2026/S2-asesorias-migratorias.pdf",
    anchor: "s2",
    icon: FileText,
    color: "#1d4ed8",
    bg: "#DBE5FA",
  },
  {
    key: "s3",
    label: "Secreto 3 — Agencia OTA",
    short: "S3 · OTA",
    file: "/docs/protocolo-2026/S3-agencia-ota.pdf",
    anchor: "s3",
    icon: FileText,
    color: "#b45309",
    bg: "#FCEBC8",
  },
  {
    key: "s4",
    label: "Manual de Carrera — 90 días",
    short: "Manual 90 días",
    file: "/docs/protocolo-2026/S4-manual-de-carrera-90-dias.pdf",
    anchor: "calendario",
    icon: FileText,
    color: "#6d28d9",
    bg: "#ECDFFB",
  },
];

const Protocolo2026Page = () => {
  const [scrolled, setScrolled] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    document.title = "Protocolo Maestro 2026 — Club Viajemos / Travel Empire";
    const onScroll = () => setScrolled(window.scrollY > 200);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: "Protocolo Maestro 2026 — Club Viajemos", url }); } catch {}
    } else {
      const wa = `https://wa.me/?text=${encodeURIComponent("Mira el Protocolo Maestro 2026 de Club Viajemos / Travel Empire: " + url)}`;
      window.open(wa, "_blank");
    }
  };

  const openAttachment = (att) => {
    // 1) Open PDF in new tab
    window.open(att.file, "_blank", "noopener,noreferrer");
    // 2) Scroll iframe to the matching section by reloading with anchor
    if (iframeRef.current) {
      iframeRef.current.src = `/protocolo-2026.html#${att.anchor}`;
    }
  };

  return (
    <div data-testid="protocolo-2026-page" className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top bar */}
      <header className={`border-b border-slate-200 bg-white/95 backdrop-blur sticky top-0 z-50 transition-shadow ${scrolled ? "shadow-md" : ""}`}>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/" data-testid="back-home" className="flex items-center gap-1 text-slate-500 hover:text-[#1D1D1B] text-sm font-medium">
            <ArrowLeft className="h-4 w-4" /> Inicio
          </Link>
          <div className="w-px h-5 bg-slate-200" />
          <p className="text-sm font-semibold text-[#1D1D1B] truncate flex-1">Protocolo Maestro 2026</p>
          <button
            type="button"
            onClick={handleShare}
            data-testid="share-protocolo"
            className="flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold px-3 py-1.5 rounded-lg transition-colors"
          >
            <Share2 className="h-3 w-3" /> Compartir
          </button>
        </div>

        {/* Attachments bar */}
        <div className="border-t border-slate-100 bg-gradient-to-r from-slate-50 to-white">
          <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-2 overflow-x-auto">
            <p className="text-[10px] font-bold uppercase tracking-[2px] text-slate-500 whitespace-nowrap pr-1 hidden md:block">
              Archivos adjuntos
            </p>
            {ATTACHMENTS.map((att) => {
              const Icon = att.icon;
              return (
                <button
                  key={att.key}
                  type="button"
                  onClick={() => openAttachment(att)}
                  data-testid={`pdf-${att.key}`}
                  title={`Abrir ${att.label} (PDF)`}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap border transition-all hover:scale-[1.03] hover:shadow-sm"
                  style={{
                    color: att.color,
                    backgroundColor: att.bg,
                    borderColor: `${att.color}33`,
                  }}
                >
                  <Icon className="h-3 w-3" />
                  {att.short}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      {/* Embed the HTML protocol */}
      <main className="flex-1 w-full">
        <iframe
          ref={iframeRef}
          src="/protocolo-2026.html"
          title="Protocolo Maestro 2026"
          data-testid="protocolo-iframe"
          className="w-full block"
          style={{ height: "calc(100vh - 56px - 48px - 88px)", border: "none" }}
        />
      </main>

      {/* Sticky CTA bottom — $27 funnel */}
      <div className="sticky bottom-0 z-50 bg-gradient-to-r from-[#1D1D1B] via-[#252548] to-[#1D1D1B] border-t-2 border-[#c9a227]/40 shadow-[0_-10px_40px_rgba(0,0,0,0.3)]">
        <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col sm:flex-row items-center gap-3">
          <div className="flex-1 text-center sm:text-left">
            <p className="text-[10px] text-[#c9a227] font-mono tracking-[2px] uppercase">¿Listo para entrar al sistema?</p>
            <p className="text-white text-sm font-bold leading-tight">
              El Manual completo de 270 paginas + Adendum + 5 Secretos por solo
              <span className="text-[#c9a227]"> $27 USD</span> <span className="text-slate-400 line-through text-xs">$297</span>
            </p>
          </div>
          <Link
            to="/manual27"
            data-testid="cta-protocolo-manual27"
            className="group flex items-center gap-2 bg-gradient-to-r from-[#c9a227] to-[#e0b835] hover:from-[#e0b835] hover:to-[#c9a227] text-[#0a0a14] font-black text-sm px-5 py-3 rounded-xl uppercase tracking-wider transition-all hover:scale-[1.03]"
          >
            <Lock className="h-4 w-4" /> Acceder por $27
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Protocolo2026Page;
